import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/PageHeader';
import { StatusBadge } from '@/components/StatusBadge';
import { apiService } from '@/services/apiService';
import { TrendingUp, AlertCircle, CheckCircle2, Activity, FolderKanban, ListChecks, ShieldAlert } from 'lucide-react';
import { cn } from '@/lib/utils';

export default function HomePage() {
  const [stats, setStats] = useState(null);
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [agents, setAgents] = useState([]);
  const [securityStats, setSecurityStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsData, projectsData, tasksData, agentsData, secData] = await Promise.all([
        apiService.getStats(),
        apiService.getProjects(),
        apiService.getTasks(),
        apiService.getAgents(),
        apiService.getSecuritySummary()
      ]);
      setStats(statsData);
      // Incluir projetos não concluídos (ativo, producao, em_andamento, planejamento)
      setProjects(projectsData.filter(p => !['concluido', 'cancelado', 'arquivado'].includes(p.status)).slice(0, 3));
      // Ordenar tarefas por data de criação (mais recentes primeiro)
      const sortedTasks = tasksData.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setTasks(sortedTasks.slice(0, 5));
      setAgents(agentsData);
      setSecurityStats(secData);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-muted-foreground">Carregando...</div>
      </div>
    );
  }

  return (
    <div data-testid="home-page">
      <PageHeader
        title="Mission Control"
        description="Visão geral do ecossistema multiagente"
      />

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 mb-6">
        <MetricCard
          title="Projetos Ativos"
          value={stats.active_projects}
          icon={TrendingUp}
          color="emerald"
          testId="metric-projects"
        />
        <MetricCard
          title="Tarefas Pendentes"
          value={stats.pending_tasks}
          icon={AlertCircle}
          color="blue"
          testId="metric-tasks"
        />
        <MetricCard
          title="Tarefas Concluídas"
          value={stats.completed_tasks}
          icon={CheckCircle2}
          color="purple"
          testId="metric-completed"
        />
        <MetricCard
          title="Agentes Ativos"
          value={agents.filter(a => a.ativo).length}
          icon={Activity}
          color="amber"
          testId="metric-agents"
        />
      </div>

      {/* Mini Security Status */}
      {securityStats && (
        <div
          className="mb-6 rounded-md border border-red-500/15 bg-red-500/5 backdrop-blur-sm p-4 cursor-pointer hover:border-red-500/30 transition-all"
          onClick={() => navigate('/guards')}
          data-testid="security-mini-status"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ShieldAlert className="h-5 w-5 text-red-400" />
              <span className="font-heading text-sm font-bold uppercase tracking-wider">Segurança VPS</span>
            </div>
            <div className="flex items-center gap-4 text-xs">
              <div className="flex items-center gap-1.5">
                <div className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
                <span className="text-emerald-400 font-bold uppercase">Sys Online</span>
              </div>
              {securityStats.unresolved > 0 && (
                <span className="text-red-400 font-mono font-bold">{securityStats.unresolved} pendente{securityStats.unresolved > 1 ? 's' : ''}</span>
              )}
              <span className="text-muted-foreground font-mono">{securityStats.blocked_ips} IPs bloqueados</span>
            </div>
          </div>
        </div>
      )}

      {/* Status dos Agentes */}
      <div className="mb-6">
        <h2 className="font-heading text-lg font-bold uppercase tracking-wider mb-4 flex items-center gap-2">
          <Activity className="h-5 w-5 text-primary" />
          Status dos Agentes
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {agents.map((agent) => (
            <div
              key={agent.id}
              className="rounded-lg border border-border/50 bg-card/40 backdrop-blur-sm hover:border-primary/30 transition-all cursor-pointer flex flex-col"
              onClick={() => navigate('/agentes')}
              data-testid="agent-status-card"
            >
              {/* Header com avatar + nome - altura fixa */}
              <div className="p-4 flex items-center gap-3">
                {/* Avatar aumentado: h-12 w-12 */}
                <div
                  className="h-12 w-12 rounded-md bg-cover bg-center border-2 flex-shrink-0"
                  style={{
                    backgroundImage: `url(${agent.avatar})`,
                    borderColor: agent.color,
                  }}
                />
                <div className="flex-1 min-w-0">
                  <h3 className="font-heading text-base font-bold leading-tight" style={{ color: agent.color }}>
                    {agent.name}
                  </h3>
                  <p className="text-[10px] text-muted-foreground uppercase leading-tight mt-0.5 truncate">{agent.specialty}</p>
                </div>
                {agent.ativo ? (
                  <CheckCircle2 className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                ) : (
                  <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Resumo de Projetos */}
        <div className="rounded-md border border-border/50 bg-card/40 backdrop-blur-sm" data-testid="projects-summary">
          <div className="border-b border-border/50 p-4 bg-muted/10 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FolderKanban className="h-5 w-5 text-primary" />
              <h2 className="font-heading text-base font-semibold uppercase tracking-wider">Projetos Recentes</h2>
            </div>
            <button
              onClick={() => navigate('/projetos')}
              className="text-xs text-primary hover:underline uppercase"
              data-testid="view-all-projects-btn"
            >
              Ver Todos
            </button>
          </div>
          <div className="p-4">
            {projects.length === 0 ? (
              <p className="text-sm text-muted-foreground">Nenhum projeto ativo</p>
            ) : (
              <div className="space-y-2">
                {projects.map((project) => (
                  <div
                    key={project.id}
                    className="rounded-md border border-border/50 p-3 hover:bg-muted/20 transition-colors cursor-pointer"
                    onClick={() => navigate(`/projetos/${project.id}`)}
                    data-testid="project-item"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-medium text-sm truncate">{project.name}</h3>
                      <StatusBadge status={project.status} />
                    </div>
                    <p className="text-xs text-muted-foreground line-clamp-1">{project.goal}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Resumo de Tarefas */}
        <div className="rounded-md border border-border/50 bg-card/40 backdrop-blur-sm" data-testid="tasks-summary">
          <div className="border-b border-border/50 p-4 bg-muted/10 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ListChecks className="h-5 w-5 text-primary" />
              <h2 className="font-heading text-base font-semibold uppercase tracking-wider">Tarefas Recentes</h2>
            </div>
            <button
              onClick={() => navigate('/tarefas')}
              className="text-xs text-primary hover:underline uppercase"
              data-testid="view-all-tasks-btn"
            >
              Ver Todas
            </button>
          </div>
          <div className="p-4">
            {tasks.length === 0 ? (
              <p className="text-sm text-muted-foreground">Nenhuma tarefa</p>
            ) : (
              <div className="space-y-2">
                {tasks.map((task) => (
                  <div
                    key={task.id}
                    className="rounded-md border border-border/50 p-3 hover:bg-muted/20 transition-colors cursor-pointer"
                    onClick={() => navigate('/tarefas')}
                    data-testid="task-item"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <StatusBadge status={task.status} />
                      <h3 className="font-medium text-xs truncate flex-1">{task.title}</h3>
                    </div>
                    <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                      <StatusBadge status={task.risk_level} className="text-[9px]" />
                      <span className="uppercase">P{task.priority}</span>
                      <span>•</span>
                      <span className="uppercase font-mono">{task.owner_agent}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function MetricCard({ title, value, icon: Icon, color, testId }) {
  const colorClasses = {
    emerald: 'border-emerald-500/20 bg-emerald-500/5',
    blue: 'border-blue-500/20 bg-blue-500/5',
    purple: 'border-purple-500/20 bg-purple-500/5',
    amber: 'border-amber-500/20 bg-amber-500/5',
  };

  const iconColorClasses = {
    emerald: 'text-emerald-500',
    blue: 'text-blue-500',
    purple: 'text-purple-500',
    amber: 'text-amber-500',
  };

  return (
    <div
      className={cn('rounded-md border p-4 backdrop-blur-sm', colorClasses[color])}
      data-testid={testId}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[10px] font-medium uppercase tracking-widest text-muted-foreground">{title}</p>
          <p className="mt-1.5 font-heading text-2xl font-bold">{value}</p>
        </div>
        <Icon className={cn('h-7 w-7', iconColorClasses[color])} />
      </div>
    </div>
  );
}
